import React, { useEffect } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { X } from "lucide-react";

const PrintStyles = createGlobalStyle`
  @media print {
    @page {
      size: A4;
      margin: 15mm;
    }

    * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    body {
      margin: 0;
      padding: 0;
    }

    body * {
      visibility: hidden;
    }

    #health-report-modal,
    #health-report-modal * {
      visibility: visible;
    }

    #health-report-modal {
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;

  @media print {
    background-color: white;
    position: static;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 210mm;
  height: 297mm;
  max-width: 90vw;
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: visible;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

  @media print {
    width: 100%;
    height: auto;
    max-width: 100%;
    max-height: none;
    box-shadow: none;
    overflow: visible;
    border-radius: 0;
    margin: 0;
    padding: 0;
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: 5vh;
  right: 6vw;
  background: white;
  border: none;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1001;

  &:hover {
    background-color: #f3f4f6;
  }

  @media print {
    display: none;
  }
`;

const PrintButton = styled.button`
  position: fixed;
  top: 5vh;
  right: 12vw;
  background: ${props => props.disabled ? '#9CA3AF' : '#00B5B8'};
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  font-size: 14px;
  font-weight: 500;
  opacity: ${props => props.disabled ? 0.6 : 1};

  &:hover {
    background-color: ${props => props.disabled ? '#9CA3AF' : '#0891B2'};
  }

  @media print {
    display: none;
  }
`;

const ReportContainer = styled.div`
  padding: 2rem;
  font-family: 'Sarabun', sans-serif;
  overflow: visible;

  @media print {
    padding: 10mm;
    width: 210mm;
    box-sizing: border-box;
    transform: scale(1.13);
    transform-origin: top left;
  }
`;

const PageNumber = styled.div`
  position: relative;
  text-align: right;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  margin-bottom: 1rem;
`;

const PageBreak = styled.div`
  @media screen {
    margin-bottom: 2rem;
    padding-bottom: 2rem;
    border-bottom: 2px dashed #E5E7EB;
  }

  @media print {
    page-break-after: always;
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const LogoImage = styled.img`
  height: 60px;
  width: auto;
`;

const TitleSection = styled.div`
  text-align: right;
  flex: 1;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
  margin-bottom: 0.25rem;
`;

const Subtitle = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #374151;
`;

const PatientInfoSection = styled.div`
  margin-bottom: 2rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const InfoRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #111827;
`;

const InfoValue = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #111827;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #E5E7EB;
  margin: 1.5rem 0;
`;

const ContentSection = styled.div`
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
  overflow: visible;
`;

const LeftSection = styled.div`
  flex: 0 0 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const HumanImage = styled.img`
  width: 230px;
  height: auto;
  margin-bottom: 1rem;
`;

const RightSection = styled.div`
  flex: 0 0 400px;
  overflow: visible;
`;

const ResultHeader = styled.div`
  background-image: url('/bg_headbar.png');
  background-size: contain;
  background-position: left center;
  background-repeat: no-repeat;
  color: #000000;
  padding-top: 0.5rem;
  padding-bottom: 1.5rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  font-size: 18px;
  font-weight: 600;
  position: relative;
  margin-bottom: -1.5rem;
  margin-left: -3.4%;
  z-index: 9999;
  overflow: visible;
  min-height: 50px;
`;

const ResultTable = styled.div`
  border: 1px solid #E5E7EB;
  border-top: none;
  border-radius: 0 0 0 0;
  overflow: visible;
`;

const ResultRow = styled.div`
  display: flex;
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }
`;

const ResultRowFull = styled.div`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }
`;

const ResultFullTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const ResultFullContent = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  padding-left: 1.5rem;
`;

const ResultLabel = styled.div`
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 14px;
  font-weight: ${props => props.bold ? '600' : '400'};
  color: #111827;
  background-color: white;
  text-align: left;
`;

const ResultValue = styled.div`
  flex: 1;
  padding: 0.75rem 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background-color: white;
`;

const NoteSection = styled.div`
  margin-top: 1.5rem;
`;

const NoteTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const StatusBadge = styled.span`
  display: inline-block;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
`;

const AdditionalSection = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InfoBox = styled.div`
  border: 2px solid #67C3F3;
  border-radius: 1rem;
  padding: 1.5rem;
`;

const InfoBoxHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const InfoIcon = styled.img`
  width: 40px;
  height: 40px;
`;

const InfoBoxTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const InfoBoxContent = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: #374151;
  line-height: 1.6;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const RiskButton = styled.button`
  background-color: transparent;
  border: 1px solid #67C3F3;
  color: #111827;
  padding: 0.5rem 1.5rem;
  font-size: 14px;
  font-weight: 400;
  cursor: default;
`;

const BloodTestTableWrapper = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const BloodTestTable = styled.div`
  border-radius: 0.5rem;
  overflow: hidden;
  width: 80%;
`;

const BloodTestHeader = styled.div`
  background-image: url('/bg_headbar.png');
  background-size: contain;
  background-position: left center;
  background-repeat: no-repeat;
  color: #000000;
  padding-top: 0.1rem;
  padding-bottom: 1.25rem;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
  font-size: 16px;
  font-weight: 600;
  width: 40%;
  position: relative;
  margin-bottom: -1.5rem;
  align-self: flex-start;
  margin-left: 8.2%;
  z-index: 9999;
`;

const BloodTestContent = styled.div`
  border: 1px solid #E5E7EB;
  border-radius: 0 0.5rem 0.5rem 0.5rem;
  overflow: hidden;
  width: 80%;
`;

const BloodTestRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  border-bottom: 1px solid #E5E7EB;

  &:last-child {
    border-bottom: none;
  }
`;

const BloodTestCell = styled.div`
  padding: 0.75rem 1rem;
  font-size: 14px;
  font-weight: ${props => props.header ? '600' : '400'};
  color: ${props => props.header ? '#111827' : '#111827'};
  background-color: white;
  text-align: ${props => props.center ? 'center' : 'left'};
`;

const AlertValue = styled.span`
  color: #DC2626;
  font-weight: 600;

  &::before {
    content: '⚠ ';
  }
`;

const UrineTestNote = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  font-size: 14px;
  color: #DC2626;
  width: 100%;
  padding-left: 10%;

  &::before {
    content: '⚠';
    font-size: 16px;
  }
`;

const HealthReportModal = ({ isOpen, onClose, recordData }) => {
  if (!isOpen) return null;

  // ตรวจสอบว่าสถานะเป็น "อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว" หรือไม่
  const isApproved = recordData?.status === 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว';

  // แปลงข้อมูลจาก recordData
  const data = {
    patientType: "บริษัท ไทยร่วมพัฒนา",
    patientName: recordData?.patientName || "นายสมชาย ใจดี",
    age: recordData?.age || 45,
    gender: recordData?.gender || "ชาย",
    hn: recordData?.hn || "VN-XXXXX-03",
    date: recordData?.date || "16 มิถุนายน 2568",
    weight: "70 กิโลกรัม",
    height: "165 เซนติเมตร",
    bmi: "29.60 กก./ม.",
    bmiStatus: "อ้วนเล็กน้อย",
    bloodPressure: "149/90 มม.ปรอก",
    pulse: "120 ครั้ง/นาที",
    diseases: recordData?.diseases || "ไม่มี",
    foodAllergies: "อาหารทะเล, นมวัว, ถั่วเหลือง"
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePrint = () => {
    if (isApproved) {
      window.print();
    }
  };

  return (
    <>
      <PrintStyles />
      <ModalOverlay onClick={handleOverlayClick}>
        <PrintButton onClick={handlePrint} disabled={!isApproved}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 6 2 18 2 18 9"></polyline>
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
            <rect x="6" y="14" width="12" height="8"></rect>
          </svg>
          Print
        </PrintButton>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>

        <ModalContent id="health-report-modal">
        <ReportContainer>
          {/* หน้าที่ 1 */}
          <PageNumber>1</PageNumber>
          <Header>
            <LogoSection>
              <LogoImage src="/logo_mfuWC.png" alt="MFU Wellness Center Logo" />
            </LogoSection>
            <TitleSection>
              <Title>รายงานผลตรวจสุขภาพ (Health check up report)</Title>
              <Subtitle>แพทย์ผู้ตรวจ นพ. นพพงศ์ ถนอมศรี</Subtitle>
            </TitleSection>
          </Header>

          <PatientInfoSection>
            <InfoRow>
              <InfoLabel><strong>ประเภท/หน่วยงาน:</strong> {data.patientType}</InfoLabel>
              <InfoLabel><strong>HN:</strong> {data.hn}</InfoLabel>
            </InfoRow>
            <InfoRow>
              <InfoRowLeft>
                <InfoLabel><strong>ชื่อ-นามสกุล:</strong> {data.patientName}</InfoLabel>
                <InfoLabel><strong>อายุ:</strong> {data.age} ปี</InfoLabel>
                <InfoLabel><strong>เพศ:</strong> {data.gender}</InfoLabel>
              </InfoRowLeft>
              <InfoLabel><strong>ตรวจวันที่:</strong> {data.date}</InfoLabel>
            </InfoRow>
          </PatientInfoSection>

          <Divider />

          <ContentSection>
            <LeftSection>
              <HumanImage src="/human.png" alt="Human Body" />
            </LeftSection>

            <RightSection>
              <ResultHeader>
                ผลการตรวจสุขภาพร่างกายทั่วไป
              </ResultHeader>
              <ResultTable>
                <ResultRow>
                  <ResultLabel>น้ำหนัก (Weight)</ResultLabel>
                  <ResultValue>{data.weight}</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>ส่วนสูง (Height)</ResultLabel>
                  <ResultValue>{data.height}</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>ดัชนีมวลกาย (BMI)</ResultLabel>
                  <ResultValue>{data.bmi}</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>• ผลการประเมิน BMI</ResultLabel>
                  <ResultValue>
                    <StatusBadge>{data.bmiStatus}</StatusBadge>
                  </ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>ความดันโลหิต (BP)</ResultLabel>
                  <ResultValue>{data.bloodPressure}</ResultValue>
                </ResultRow>
                <ResultRow>
                  <ResultLabel>ชีพจร (Pulse)</ResultLabel>
                  <ResultValue>{data.pulse}</ResultValue>
                </ResultRow>
                <ResultRowFull>
                  <ResultFullTitle>โรคประจำตัว</ResultFullTitle>
                  <ResultFullContent>• {data.diseases}</ResultFullContent>
                </ResultRowFull>
                <ResultRowFull>
                  <ResultFullTitle>ประวัติแพ้ยา ,แพ้อาหาร</ResultFullTitle>
                  <ResultFullContent>• {data.foodAllergies}</ResultFullContent>
                </ResultRowFull>
              </ResultTable>
            </RightSection>
          </ContentSection>

          <AdditionalSection>
            {/* สรุปความเสี่ยงสุขภาพ */}
            <InfoBox>
              <InfoBoxHeader>
                <InfoIcon src="/ic_1.png" alt="Health Risk Icon" />
                <InfoBoxTitle>สรุปความเสี่ยงสุขภาพ</InfoBoxTitle>
              </InfoBoxHeader>
              <ButtonGroup>
                <RiskButton>ไขมันในเลือดค่อนข้างสูง</RiskButton>
                <RiskButton>ความดันสูง</RiskButton>
                <RiskButton>อ้วนลงพุง</RiskButton>
              </ButtonGroup>
            </InfoBox>

            {/* คำแนะนำจากแพทย์ */}
            <InfoBox>
              <InfoBoxHeader>
                <InfoIcon src="/ic_2.png" alt="Doctor Advice Icon" />
                <InfoBoxTitle>คำแนะนำจากแพทย์</InfoBoxTitle>
              </InfoBoxHeader>
              <InfoBoxContent>
                สิ่งสำคัญคือ พบแพทย์ตามนัดอย่างสม่ำเสมอ และ ทานยาตามที่แพทย์สั่งอย่างเคร่งครัด ห้ามหยุดยาเองเด็ดขาด นอกจากนี้ควรงดเครื่องดื่มแอลกอฮอล์และการสูบบุหรี่ เพื่อลดความเสี่ยงภาวะแทรกซ้อน และควรเรียนรู้เกี่ยวกับโรคเพื่อดูแลตัวเองได้อย่างถูกต้องและมีประสิทธิภาพ
              </InfoBoxContent>
            </InfoBox>

            {/* คำแนะนำด้านโภชนาการ */}
            <InfoBox>
              <InfoBoxHeader>
                <InfoIcon src="/ic_2.png" alt="Nutrition Advice Icon" />
                <InfoBoxTitle>คำแนะนำด้านโภชนาการ</InfoBoxTitle>
              </InfoBoxHeader>
              <InfoBoxContent>
                ควรลดอาหารรสจัด โดยเฉพาะรสเค็ม หวาม และมัน เช่น ขนมหวาน น้ำอัดลม และอาหารทอดเพิ่มผักใบเขียวและผลไม้รสไม่หวาน และ เลือกทานคาร์โบไฮเดรตเชิงซ้อน เช่น ข้าวกล้อง และ ขนมปังโฮลวีท ควร ลดเนื้อสัตว์ติดมันและหันมาทานปลา อกไก่ หรือเต้าหู้แทน
              </InfoBoxContent>
            </InfoBox>
          </AdditionalSection>

          <PageBreak />

          {/* หน้าที่ 2 */}
          <PageNumber>2</PageNumber>
          <Header>
            <LogoSection>
              <LogoImage src="/logo_mfuWC.png" alt="MFU Wellness Center Logo" />
            </LogoSection>
            <TitleSection>
              <Title>รายงานผลตรวจสุขภาพ (Health check up report)</Title>
              <Subtitle>แพทย์ผู้ตรวจ นพ. นพพงศ์ ถนอมศรี</Subtitle>
            </TitleSection>
          </Header>

          <PatientInfoSection>
            <InfoRow>
              <InfoLabel><strong>ประเภท/หน่วยงาน:</strong> {data.patientType}</InfoLabel>
              <InfoLabel><strong>HN:</strong> {data.hn}</InfoLabel>
            </InfoRow>
            <InfoRow>
              <InfoRowLeft>
                <InfoLabel><strong>ชื่อ-นามสกุล:</strong> {data.patientName}</InfoLabel>
                <InfoLabel><strong>อายุ:</strong> {data.age} ปี</InfoLabel>
                <InfoLabel><strong>เพศ:</strong> {data.gender}</InfoLabel>
              </InfoRowLeft>
              <InfoLabel><strong>ตรวจวันที่:</strong> {data.date}</InfoLabel>
            </InfoRow>
          </PatientInfoSection>

          <Divider />

          <AdditionalSection>
            {/* คำแนะนำด้านการออกกำลังกาย */}
            <InfoBox>
              <InfoBoxHeader>
                <InfoIcon src="/ic_2.png" alt="Exercise Advice Icon" />
                <InfoBoxTitle>คำแนะนำด้านการออกกำลังกาย</InfoBoxTitle>
              </InfoBoxHeader>
              <InfoBoxContent>
                ควรออกกำลังกายสม่ำเสมอ อย่างน้อยวันละ 30 นาที 3-5 วันต่อสัปดาห์ เช่น เดินเร็ว ปั่นจักรยาน หรือว่ายน้ำ เลือกประเภทการออกกำลังกายที่เหมาะสม และ ไม่หักโหมจนเกินไป ควรเริ่มต้นจากเบาๆ แล้วค่อยๆ เพิ่มความหนักขึ้นการออกกำลังกายจะช่วยควบคุมน้ำหนักและระดับน้ำตาลในเลือดได้ดี
              </InfoBoxContent>
            </InfoBox>

            {/* คำแนะนำด้านการนอน */}
            <InfoBox>
              <InfoBoxHeader>
                <InfoIcon src="/ic_2.png" alt="Sleep Advice Icon" />
                <InfoBoxTitle>คำแนะนำด้านการนอน</InfoBoxTitle>
              </InfoBoxHeader>
              <InfoBoxContent>
                ควรนอนหลับให้เพียงพอ วันละ 7-8 ชั่วโมง และ เข้านอน-ตื่นนอนให้เป็นเวลา ทุกวัน เพื่อให้ร่างกายมีระบบการทำงานที่สม่ำเสมอ ควรจัดสภาพแวดล้อมในห้องนอนให้เหมาะสม และ งดเล่นโทรศัพท์ หรือดูทีวีก่อนนอนอย่างน้อย 30 นาที การนอนที่มีคุณภาพช่วยลดความเครียดและความดันโลหิตได้
              </InfoBoxContent>
            </InfoBox>
          </AdditionalSection>

          {/* ตารางความสมบูรณ์ของเม็ดเลือด */}
          <BloodTestTableWrapper>
            <BloodTestHeader>ความสมบูรณ์ของเม็ดเลือด</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เม็ดเลือดแดง</BloodTestCell>
                <BloodTestCell center><AlertValue>17.6</AlertValue></BloodTestCell>
                <BloodTestCell center>13 - 17 g/dL</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ฮีโมโกลบิน</BloodTestCell>
                <BloodTestCell center>14.3</BloodTestCell>
                <BloodTestCell center>13.0 - 17.5 g/dL</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ฮีมาโตคริต</BloodTestCell>
                <BloodTestCell center>43</BloodTestCell>
                <BloodTestCell center>40 - 54%</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เม็ดเลือดขาว</BloodTestCell>
                <BloodTestCell center>6,840</BloodTestCell>
                <BloodTestCell center>4,500 - 10,000 cells/ml</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เกล็ดเลือด</BloodTestCell>
                <BloodTestCell center>260,000</BloodTestCell>
                <BloodTestCell center>150,000 - 450,000 cells/mm³</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          {/* ตารางระดับน้ำตาลในเลือด */}
          <BloodTestTableWrapper>
            <BloodTestHeader>ระดับน้ำตาลในเลือด</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>น้ำตาลในเลือด</BloodTestCell>
                <BloodTestCell center><AlertValue>103</AlertValue></BloodTestCell>
                <BloodTestCell center>70 - 99 mg/dl</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          <PageBreak />

          {/* หน้าที่ 3 */}
          <PageNumber>3</PageNumber>
          <Header>
            <LogoSection>
              <LogoImage src="/logo_mfuWC.png" alt="MFU Wellness Center Logo" />
            </LogoSection>
            <TitleSection>
              <Title>รายงานผลตรวจสุขภาพ (Health check up report)</Title>
              <Subtitle>แพทย์ผู้ตรวจ นพ. นพพงศ์ ถนอมศรี</Subtitle>
            </TitleSection>
          </Header>

          <PatientInfoSection>
            <InfoRow>
              <InfoLabel><strong>ประเภท/หน่วยงาน:</strong> {data.patientType}</InfoLabel>
              <InfoLabel><strong>HN:</strong> {data.hn}</InfoLabel>
            </InfoRow>
            <InfoRow>
              <InfoRowLeft>
                <InfoLabel><strong>ชื่อ-นามสกุล:</strong> {data.patientName}</InfoLabel>
                <InfoLabel><strong>อายุ:</strong> {data.age} ปี</InfoLabel>
                <InfoLabel><strong>เพศ:</strong> {data.gender}</InfoLabel>
              </InfoRowLeft>
              <InfoLabel><strong>ตรวจวันที่:</strong> {data.date}</InfoLabel>
            </InfoRow>
          </PatientInfoSection>

          <Divider />

          {/* ตารางไขมันในเลือด */}
          <BloodTestTableWrapper>
            <BloodTestHeader>ไขมันในเลือด</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>คอเลสเตอรอล</BloodTestCell>
                <BloodTestCell center><AlertValue>211</AlertValue></BloodTestCell>
                <BloodTestCell center>120 - 200 mg/dL</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ไตรกลีเซอไรด์</BloodTestCell>
                <BloodTestCell center>138</BloodTestCell>
                <BloodTestCell center>45 - 150mg/dL</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>คอเลสเตอรอลดี (HDL)</BloodTestCell>
                <BloodTestCell center>42</BloodTestCell>
                <BloodTestCell center>30 - 60 mg/dL</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>คอเลสเตอรอลไม่ดี (LDL)</BloodTestCell>
                <BloodTestCell center>141</BloodTestCell>
                <BloodTestCell center>60-130 mg/dL</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          {/* ตารางการทำงานของไต */}
          <BloodTestTableWrapper>
            <BloodTestHeader>การทำงานของไต</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>Creatinine</BloodTestCell>
                <BloodTestCell center><AlertValue>1.22</AlertValue></BloodTestCell>
                <BloodTestCell center>0.7 - 1.2 mg/dl</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>BUN , Urea</BloodTestCell>
                <BloodTestCell center>14</BloodTestCell>
                <BloodTestCell center>7 - 20 mg/dl</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>eGFR</BloodTestCell>
                <BloodTestCell center>91.64</BloodTestCell>
                <BloodTestCell center>mL/min/1.73m²</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          {/* ตารางการทำงานของตับ */}
          <BloodTestTableWrapper>
            <BloodTestHeader>การทำงานของตับ</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>SGPT</BloodTestCell>
                <BloodTestCell center>25</BloodTestCell>
                <BloodTestCell center>5 - 35 U/L</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>SGOT</BloodTestCell>
                <BloodTestCell center>29</BloodTestCell>
                <BloodTestCell center>0 - 40 U/L</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>Alkaline Phosphatase</BloodTestCell>
                <BloodTestCell center>115</BloodTestCell>
                <BloodTestCell center>40 - 120 U/L</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          {/* ตารางกรดยูริค */}
          <BloodTestTableWrapper>
            <BloodTestHeader>กรดยูริค</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>Uric Acid</BloodTestCell>
                <BloodTestCell center>6.4</BloodTestCell>
                <BloodTestCell center>3.5 - 7 mg/dL</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          <PageBreak />

          {/* หน้าที่ 4 */}
          <PageNumber>4</PageNumber>
          <Header>
            <LogoSection>
              <LogoImage src="/logo_mfuWC.png" alt="MFU Wellness Center Logo" />
            </LogoSection>
            <TitleSection>
              <Title>รายงานผลตรวจสุขภาพ (Health check up report)</Title>
              <Subtitle>แพทย์ผู้ตรวจ นพ. นพพงศ์ ถนอมศรี</Subtitle>
            </TitleSection>
          </Header>

          <PatientInfoSection>
            <InfoRow>
              <InfoLabel><strong>ประเภท/หน่วยงาน:</strong> {data.patientType}</InfoLabel>
              <InfoLabel><strong>HN:</strong> {data.hn}</InfoLabel>
            </InfoRow>
            <InfoRow>
              <InfoRowLeft>
                <InfoLabel><strong>ชื่อ-นามสกุล:</strong> {data.patientName}</InfoLabel>
                <InfoLabel><strong>อายุ:</strong> {data.age} ปี</InfoLabel>
                <InfoLabel><strong>เพศ:</strong> {data.gender}</InfoLabel>
              </InfoRowLeft>
              <InfoLabel><strong>ตรวจวันที่:</strong> {data.date}</InfoLabel>
            </InfoRow>
          </PatientInfoSection>

          <Divider />

          {/* ตารางผลการตรวจปัสสาวะ */}
          <BloodTestTableWrapper>
            <BloodTestHeader>ผลการตรวจปัสสาวะ</BloodTestHeader>
            <BloodTestContent>
              <BloodTestRow>
                <BloodTestCell header>รายการตรวจ</BloodTestCell>
                <BloodTestCell header center>ผลการตรวจ</BloodTestCell>
                <BloodTestCell header center>ค่าปกติ</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>สีของปัสสาวะ</BloodTestCell>
                <BloodTestCell center>Yellow</BloodTestCell>
                <BloodTestCell center>Yellow</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>pH</BloodTestCell>
                <BloodTestCell center>6.2</BloodTestCell>
                <BloodTestCell center>4.5 - 8.0</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ความถ่วงจำเพาะ</BloodTestCell>
                <BloodTestCell center>1.025</BloodTestCell>
                <BloodTestCell center>1.003 - 1.030</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>โปรตีน</BloodTestCell>
                <BloodTestCell center>1+</BloodTestCell>
                <BloodTestCell center>Negative, Trace</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>น้ำตาล</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>คีโตน</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เลือด</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เม็ดเลือดขาว</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ไนเตรต</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>บิลิรูบิน</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>ยูโรบิลินโนเจน</BloodTestCell>
                <BloodTestCell center>0.3</BloodTestCell>
                <BloodTestCell center>0.2 - 1.0 mg/dL</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เซลล์เม็ดเลือดแดง</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
              </BloodTestRow>
              <BloodTestRow>
                <BloodTestCell>เซลล์เม็ดเลือดขาว</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
                <BloodTestCell center>Negative</BloodTestCell>
              </BloodTestRow>
            </BloodTestContent>
          </BloodTestTableWrapper>

          <UrineTestNote>หมายเหตุ: ค่าผิดปกติ</UrineTestNote>
        </ReportContainer>
      </ModalContent>
    </ModalOverlay>
    </>
  );
};

export default HealthReportModal;
